/**
 * Active Processes Card Component - Meus Processos Ativos
 * Displays list of active processes with progress tracking
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, AlertTriangle, ChevronRight, Clock, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { differenceInDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Process {
  id: string;
  object_summary: string;
  current_stage: string;
  status: string;
  progress_percentage?: number;
  deadline_date?: string;
  urgency_level?: string;
  process_number: string;
  created_at: string;
}

interface ActiveProcessesCardProps {
  processes: Process[];
}

export const ActiveProcessesCard: React.FC<ActiveProcessesCardProps> = ({ processes }) => {
  const navigate = useNavigate();

  return (
    <Card className="lg:col-span-2 card-navy-glass border-navy-500">
      <CardHeader className="border-b border-navy-500">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Meus Processos Ativos
          </CardTitle>
          <Badge className="bg-primary/20 text-primary border border-primary">
            {processes.length} ativos
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-4 overflow-x-auto pb-4">
          {processes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">Nenhum processo ativo no momento.</p>
              <Button
                onClick={() => navigate("/processes/new")}
                className="mt-4 gradient-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Processo
              </Button>
            </div>
          ) : (
            processes.map((process) => (
              <div
                key={process.id}
                className="p-4 rounded-lg bg-navy-800/50 border border-navy-500 card-hover-navy transition-all cursor-pointer group"
                onClick={() => navigate(`/processes/${process.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {process.object_summary || 'Processo sem título'}
                      </h4>
                      {process.process_number && (
                        <Badge variant="outline" className="text-xs text-muted-foreground border-navy-500">
                          {process.process_number}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>{process.current_stage || 'Iniciando processo'}</span>
                      {process.created_at && (
                        <span className="flex items-center gap-1 text-xs">
                          <Calendar className="w-3 h-3" />
                          Criado em {format(new Date(process.created_at), "dd/MM/yyyy", { locale: ptBR })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {process.urgency_level === 'high' && (
                      <Badge className="bg-red-500 bg-opacity-20 text-red-400 border border-red-500">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Urgente
                      </Badge>
                    )}
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>

                {process.progress_percentage !== undefined && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progresso</span>
                      <span className="text-primary font-semibold">{process.progress_percentage}%</span>
                    </div>
                    <Progress value={process.progress_percentage} className="h-2" />
                    {process.deadline_date && (
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {differenceInDays(new Date(process.deadline_date), new Date()) >= 0 ?
                            `${differenceInDays(new Date(process.deadline_date), new Date())} dias restantes` :
                            `Atrasado por ${Math.abs(differenceInDays(new Date(process.deadline_date), new Date()))} dias`
                          }
                        </span>
                        <span className="font-medium text-foreground">
                          {process.status === 'active' ? 'Em Andamento' : process.status}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
